import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoadingIconComponent } from 'src/app/components/loading-icon/loading-icon.component';
import { IconModule } from 'src/app/icon/icon.module';
import { MaterialModule } from 'src/app/material.module';
import { ProcessService } from 'src/app/services/process.service';
import { FormButtonComponent } from "src/app/components/dynamic-form/form-button/form-button.component";
import { excelToJson } from 'src/app/shared/utils';
import { ToastService } from 'src/app/services/toast.service';

export interface IImportProcessError {
  sheet?: string;
  row?: number;
  field?: string;
  message: string;
}

export interface ImportProcessDialogResult {
  count?: number;
  errors?: IImportProcessError[];
}

@Component({
  selector: 'app-import-process-dialog',
  imports: [MaterialModule, CommonModule, IconModule, TranslateModule, LoadingIconComponent, FormButtonComponent],
  templateUrl: './import-process-dialog.component.html'
})
export class ImportProcessDialogComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  selectedFile: File | null = null;
  isLoading = false;
  isDragging = false;

  constructor(
    private dialogRef: MatDialogRef<ImportProcessDialogComponent>,
    private processService: ProcessService,
    private toastService: ToastService,
    private translate: TranslateService
  ) { }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    this.selectedFile = input.files[0];
  }

  onClose() {
    this.dialogRef.close();
  }

  async onImport() {
    if (!this.selectedFile) return;
    this.isLoading = true;
    const { isValid, errors } = await this.validateExcelData(this.selectedFile);
    if (!isValid) {
      this.isLoading = false;
      if(errors.length) { 
        this.dialogRef.close({ errors });
      }
      return;
    };

    this.processService.importProcess(this.selectedFile).subscribe({
      next: (count) => {
        this.isLoading = false;
        this.dialogRef.close();
        this.toastService.success(this.translate.instant('import started. Processing your data', { count }))
      },
      error: (error) => {
        console.error(error);
        this.isLoading = false;
      }
    });
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
    const file = event.dataTransfer?.files?.[0];
    if (!file || !file.name.endsWith('.xlsx')) return
    this.selectedFile = file;
  }

  async validateExcelData(file: File): Promise<{ isValid: boolean; errors: IImportProcessError[] }> {
    const errors: IImportProcessError[] = [];
    const excelRawData = await excelToJson(file)
    if (!excelRawData || Object.keys(excelRawData).length === 0) {
      this.toastService.error(this.translate.instant('excel file is empty or invalid'));
      this.resetFileInput();
      return { isValid: false, errors };
    }
    // remove second headers
    if(!excelRawData['Process']) {
      excelRawData['Economic allocation - ReCiPe'].shift();
      excelRawData['Energy allocation - ReCiPe'].shift();
      excelRawData['Mass allocation - ReCiPe'].shift();
      excelRawData['Economic allocation - EF3.1'].shift();
      excelRawData['Energy allocation - EF3.1'].shift();
      excelRawData['Mass allocation - EF3.1'].shift();
    }
    const processSheet = excelRawData['Process'] ?? excelRawData['Economic allocation - ReCiPe'];
    if (!Array.isArray(processSheet)) {
      this.toastService.error(this.translate.instant('excel file is empty or invalid'));
      this.resetFileInput();
      return { isValid: false, errors };
    }

    const indicatorSheetNames = ['Economic allocation - ReCiPe', 'Energy allocation - ReCiPe', 'Mass allocation - ReCiPe', 'Economic allocation - EF3.1', 'Energy allocation - EF3.1', 'Mass allocation - EF3.1'];
    const allSheets = ['Process', ...indicatorSheetNames];

    // validate if all sheets have the same number of rows
    if(!Object.keys(excelRawData).filter(sheet => indicatorSheetNames.includes(sheet)).every(sheet => excelRawData[sheet]?.length === processSheet?.length)) {
      this.toastService.error(this.translate.instant('missing processes or process indicators'));
      this.resetFileInput();
      return { isValid: false, errors };
    }
    allSheets.shift();
    if (!allSheets.every(sheet => excelRawData[sheet]?.length > 0)) {
      this.toastService.error(this.translate.instant('missing processes or process indicators'));
      this.resetFileInput();
      return { isValid: false, errors };
    }

    // validate required fields and numeric data types
    for (let i = 0; i < processSheet.length; i++) {
      const row = processSheet[i];
      // check file "Environmental impact GFLI" or export file
      const rowNumber = excelRawData['Process'] ? i + 2 : i + 3;

      const requiredFields = ['DatasetMainName'];
      for (const field of requiredFields) {
        if (!row[field]) {
          errors.push({
            sheet: excelRawData['Process'] ? 'Process' : 'Economic allocation - ReCiPe',
            row: rowNumber,
            field,
            message: this.translate.instant('missing required value')
          });
        }
      }

      // Validate Process numeric fields
      const processValidation = this.validateProcessNumericFields(row);
      if (!processValidation.isValid) {
        errors.push({
          sheet: excelRawData['Process'] ? 'Process' : 'Economic allocation - ReCiPe',
          row: rowNumber,
          field: processValidation.field,
          message: processValidation.errorMessage!
        });
      }
    }

    // Validate ProcessIndicator numeric fields in all indicator sheets
    for (const sheetName of indicatorSheetNames) {
      const sheet = excelRawData[sheetName];
      if (!Array.isArray(sheet)) continue;

      for (let i = 0; i < sheet.length; i++) {
        const row = sheet[i];
        const rowNumber = excelRawData['Process'] ? i + 2 : i + 3;
        const indicatorValidation = this.validateProcessIndicatorNumericFields(row);
        if (!indicatorValidation.isValid) {
          errors.push({
            sheet: sheetName,
            row: rowNumber,
            field: indicatorValidation.field,
            message: indicatorValidation.errorMessage!
          });
        }

        const textLengthErrors = this.validateProcessIndicatorTextLengthFields(row);
        for (const error of textLengthErrors) {
          errors.push({
            sheet: sheetName,
            row: rowNumber,
            field: error.field,
            message: error.errorMessage
          });
        }
      }
    }

    if (errors.length) {
      this.resetFileInput();
      return { isValid: false, errors };
    }

    return { isValid: true, errors: [] };
  }

  private validateProcessNumericFields(row: any): { isValid: boolean; field?: string; errorMessage?: string } {
    // YearOfPublication: short? - reasonable year range (1900-2100)
    const yearOfPublicationField = 'YearOfPublication';
    if (row[yearOfPublicationField] != null) {
      const year = this.parseNumericValue(row[yearOfPublicationField]);
      if (year === null) {
        return {
          isValid: false,
          field: yearOfPublicationField,
          errorMessage: this.translate.instant('invalid data type')
        };
      }
      if (year < 1900 || year > 2100) {
        return {
          isValid: false,
          field: yearOfPublicationField,
          errorMessage: this.translate.instant('value out of range')
        };
      }
    }

    // decimal(13, 9) fields: DryMatterContent, EnergyContent, NitrogenContent, PhosphorusContent
    const decimal13_9Fields = ['DryMatterContent', 'EnergyContent', 'NitrogenContent', 'PhosphorusContent'];
    for (const field of decimal13_9Fields) {
      if (['not applicable', 'not relevant', 'n/a', ''].includes(row[field]?.toString().toLowerCase())) {
        continue;
      }
      if (row[field] != null) {
        const value = this.parseNumericValue(row[field]);
        if (value === null) {
          return {
            isValid: false,
            field,
            errorMessage: this.translate.instant('invalid data type')
          };
        }
        const maxValue = 9999.999999999;
        if (Math.abs(value) > maxValue) {
          return {
            isValid: false,
            field,
            errorMessage: this.translate.instant('value out of range')
          };
        }
      }
    }

    // decimal(24, 12) fields: DQRTotal, DQRPrecision, DQRTime, DQRTech, DQRGeo (match Process entity)
    const decimal24_12Fields = ['DQRTotal', 'DQRPrecision', 'DQRTime', 'DQRTech', 'DQRGeo'];
    for (const field of decimal24_12Fields) {
      if (row[field] != null) {
        const value = this.parseNumericValue(row[field]);
        if (value === null) {
          return {
            isValid: false,
            field,
            errorMessage: this.translate.instant('invalid data type')
          };
        }
        const maxValue = 999999999999.999999999999;
        if (Math.abs(value) > maxValue) {
          return {
            isValid: false,
            field,
            errorMessage: this.translate.instant('value out of range')
          };
        }
      }
    }

    return { isValid: true };
  }

  private validateProcessIndicatorNumericFields(row: any): { isValid: boolean; field?: string; errorMessage?: string } {
    // All EF_* and RECIPE_* fields are decimal(38, 28)
    const indicators = 
      [
        'EF_AP', 'EF_GWPtotal', 'EF_GWPbiogenic', 'EF_GWPfossil', 'EF_GWPluluc', 'EF_GWPexcl', 'EF_GWPpeat',
        'EF_ETPfw_part1', 'EF_ETPfwinorg', 'EF_ETPfworg_part1',
        'EF_PM', 'EF_EPmarine', 'EF_EPfreshwater', 'EF_EPterrestrial',
        'EF_HTPC', 'EF_HTPCinorg', 'EF_HTPCorg', 'EF_HTPNC', 'EF_HTPNCinorg', 'EF_HTPNCorg',
        'EF_IRP', 'EF_SQP', 'EF_ODP', 'EF_POCP', 'EF_ADPfossil', 'EF_ADPminerals', 'EF_WDP',
        'RECIPE_GWP_TOT', 'RECIPE_GWP_EXCL', 'RECIPE_GWP_LUC', 'RECIPE_GWP_PEAT',
        'RECIPE_ODP', 'RECIPE_IRP', 'RECIPE_HOFP', 'RECIPE_PMFP', 'RECIPE_EOFP',
        'RECIPE_TAP', 'RECIPE_FEP', 'RECIPE_MEP', 'RECIPE_TETP', 'RECIPE_FETP', 'RECIPE_METP',
        'RECIPE_HTPc', 'RECIPE_HTPnc', 'RECIPE_LUTOT', 'RECIPE_LUTR', 'RECIPE_LOP', 'RECIPE_SOP',
        'RECIPE_FFP', 'RECIPE_WCP',

        'Ap', 'Gwptotal', 'Gwpbiogenic', 'Gwpfossil', 'Gwpluluc', 'Gwpexcl', 'Gwppeat',
        'EtpfwPart1', 'Etpfwinorg', 'EtpfworgPart1',
        'Pm', 'Epmarine', 'Epfreshwater', 'Epterrestrial',
        'Htpc', 'Htpcinorg', 'Htpcorg', 'Htpnc', 'Htpncinorg', 'Htpncorg',
        'Irp', 'Sqp', 'Odp', 'Pocp', 'Adpfossil', 'Adpminerals', 'Wdp',
        'GwpExcl', 'GwpLuc', 'GwpPeat',
        'Hofp', 'Pmfp', 'Eofp',
        'Tap', 'Fep', 'Mep', 'Tetp', 'Fetp', 'Metp',
        'Lutot', 'Lutr', 'Lop', 'Sop',
        'Ffp', 'Wcp',

        // headers used by this app's own export, so a previously exported file validates too
        'Acidification (mol H+ eq / ton product)',
        'Climate change (kg CO2 eq / ton product)',
        'Climate change - Biogenic (kg CO2 eq / ton product)',
        'Climate change - Fossil (incl peat ox) (kg CO2 eq / ton product)',
        'Climate change - Land use and LU change (kg CO2 eq / ton product)',
        'Climate change - excl LUC / peat ox (kg CO2 eq / ton product)',
        'Climate change - Fossil (only peat) (kg CO2 eq / ton product)',
        'Ecotoxicity, freshwater (CTUe / ton product)',
        'Ecotoxicity, freshwater - inorganics (CTUe / ton product)',
        'Ecotoxicity, freshwater - organics (CTUe / ton product)',
        'Particulate matter (disease inc. / ton product)',
        'Eutrophication, marine (kg N eq / ton product)',
        'Eutrophication, freshwater (kg P eq / ton product)',
        'Eutrophication, terrestrial (mol N eq / ton product)',
        'Human toxicity, cancer (CTUh / ton product)',
        'Human toxicity, cancer - inorganics (CTUh / ton product)',
        'Human toxicity, cancer - organics (CTUh / ton product)',
        'Human toxicity, non-cancer (CTUh / ton product)',
        'Human toxicity, non-cancer - inorganics (CTUh / ton product)',
        'Human toxicity, non-cancer - organics (CTUh / ton product)',
        'Ionising radiation (kBq U-235 eq / ton product)',
        'Land use (Pt / ton product)',
        'Ozone depletion (kg CFC11 eq / ton product)',
        'Photochemical ozone formation (kg NMVOC eq / ton product)',
        'Resource use, fossils (MJ / ton product)',
        'Resource use, minerals and metals (kg Sb eq / ton product)',
        'Water use (m3 depriv. / ton product)',

        'Global warming - Including LUC & Peat (kg CO2 eq / ton product)',
        'Global warming - Excluding LUC & peat(kg CO2 eq / ton product)',
        'Global warming - LUC only (kg CO2 eq / ton product)',
        'Global warming - Peat only (kg CO2 eq / ton product)',
        'Stratospheric ozone depletion (kg CFC11 eq / ton product)',
        'Ionizing radiation (kBq Co-60 eq / ton product)',
        'Ozone formation, Human health (kg NOx eq / ton product)',
        'Fine particulate matter formation (kg PM2.5 eq / ton product)',
        'Ozone formation, Terrestrial ecosystems (kg NOx eq / ton product)',
        'Terrestrial acidification (kg SO2 eq / ton product)',
        'Freshwater eutrophication (kg P eq / ton product)',
        'Marine eutrophication (kg N eq / ton product)',
        'Terrestrial ecotoxicity (kg 1,4-DCB / ton product)',
        'Freshwater ecotoxicity (kg 1,4-DCB / ton product)',
        'Marine ecotoxicity (kg 1,4-DCB / ton product)',
        'Human carcinogenic toxicity (kg 1,4-DCB / ton product)',
        'Human non-carcinogenic toxicity (kg 1,4-DCB / ton product)',
        'Land use - Total (m2a crop eq / ton product)',
        'Land use - Transformation (m2a / ton product)',
        'Land occupation (m2a / ton product)',
        'Mineral resource scarcity (kg Cu eq / ton product)',
        'Fossil resource scarcity (kg oil eq / ton product)',
        'Water consumption (m3 / ton product)'
    ];

    for (const field of indicators) {
      // Empty cell (incl. an empty text cell) is stored as NULL: skip validation.
      if (row[field] == null || row[field].toString().trim() === '') {
        continue;
      }

      const value = this.parseNumericValue(row[field]);
      if (value === null) {
        return {
          isValid: false,
          field,
          errorMessage: this.translate.instant('invalid data type')
        };
      }
      // eslint-disable-next-line no-loss-of-precision
      const maxValue = 9999999999.99999999999999999999999999;
      if (Math.abs(value) > maxValue) {
        return {
          isValid: false,
          field,
          errorMessage: this.translate.instant('value out of range')
        };
      }
    }

    return { isValid: true };
  }

  private validateProcessIndicatorTextLengthFields(row: any): Array<{ field: string; errorMessage: string }> {
    const errors: Array<{ field: string; errorMessage: string }> = [];

    const textLengthFields = [
      { field: 'EF_Class', length: 2 },
      { field: 'EF_SubClass', length: 2 },
      { field: 'EF_Subgroup', length: 3 },
      { field: 'EF_Subgroup2', length: 3 },
      { field: 'EF_Boundaries', length: 1 },
      { field: 'EF_CountryCode', length: 3 },
      { field: 'EF_StateCode', length: 2 },
      { field: 'EF_Tier', length: 2 },
      { field: 'EF_Allocation', length: 3 },
      { field: 'EF_ImpactMethod', length: 2 },

      { field: 'RECIPE_Class', length: 2 },
      { field: 'RECIPE_SubClass', length: 2 },
      { field: 'RECIPE_Subgroup', length: 3 },
      { field: 'RECIPE_Subgroup2', length: 3 },
      { field: 'RECIPE_Boundaries', length: 1 },
      { field: 'RECIPE_CountryCode', length: 3 },
      { field: 'RECIPE_StateCode', length: 2 },
      { field: 'RECIPE_Tier', length: 2 },
      { field: 'RECIPE_Allocation', length: 3 },
      { field: 'RECIPE_ImpactMethod', length: 2 },

      { field: 'Class', length: 2 },
      { field: 'SubClass', length: 2 },
      { field: 'Subgroup', length: 3 },
      { field: 'Subgroup2', length: 3 },
      { field: 'Boundaries', length: 1 },
      { field: 'Country code', length: 3 },
      { field: 'State code', length: 2 },
      { field: 'Tier', length: 2 },
      { field: 'Allocation', length: 3 },
      { field: 'Impact method', length: 2 }
    ];

    for (const { field, length } of textLengthFields) {
      const rawValue = row[field];

      if (rawValue == null || rawValue === '') continue;

      const value = String(rawValue).trim();
      if (value.length > length) {
        errors.push({
          field,
          errorMessage: this.translate.instant('length must not exceed', { length })
        });
      }
    }

    return errors;
  }

  private isNumber = (value: any): boolean => value !== null && value !== '' && !isNaN(value) && !(value instanceof Date);
  private parseNumericValue(value: any): number | null {
    if (value == null || value === '') 
      return null;
    if (this.isNumber(value)) 
      return value;
    return null;
  }

  resetFileInput() {
    this.selectedFile = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  get cancelButtonConfig() {
    return {
      text: 'cancel',
      type: 'button',
      isStroked: true,
      onClick: () => this.onClose()
    };
  }
}
